import { z } from 'zod';
import { decimalStringSchema, isoDateTimeSchema, uuidSchema } from './common.js';
import { collectionSchema } from './collections.js';
import { cookingLogEntrySchema } from './ratings.js';
import { recipeSchema } from './recipes.js';
import { locationSourceSchema, placeSchema, visitVisibilitySchema } from './tasting.js';
import { userProfileSchema } from './users.js';
export const DATA_EXPORT_FORMAT_VERSION = '1.0';
export const exportedPlaceSchema = placeSchema.omit({ visitCount: true, own: true });
/**
 * Tasting-note visits. OWNER visits are complete; CONTRIBUTOR visits belong to another
 * household member and only carry the dishes the exporting user wrote (no location).
 */
export const exportedVisitSchema = z.object({
    id: uuidSchema,
    role: z.enum(['OWNER', 'CONTRIBUTOR']),
    visibility: visitVisibilitySchema,
    householdId: uuidSchema.nullable(),
    placeId: uuidSchema.nullable(),
    visitedAt: isoDateTimeSchema,
    comment: z.string().nullable(),
    lat: z.number().nullable(),
    lng: z.number().nullable(),
    accuracyM: z.number().int().nullable(),
    locationSource: locationSourceSchema,
    city: z.string().nullable(),
    region: z.string().nullable(),
    countryCode: z.string().nullable(),
    notes: z.array(z.object({
        id: uuidSchema,
        authoredByMe: z.boolean(),
        dishName: z.string().nullable(),
        notes: z.string(),
        flavorTags: z.array(z.string()),
        rating: z.number().int().nullable(),
        photos: z.array(z.object({ assetId: uuidSchema, fileName: z.string().nullable() })),
        recipeIds: z.array(uuidSchema),
        createdAt: isoDateTimeSchema,
    })),
    createdAt: isoDateTimeSchema,
});
/**
 * What the user contributed to the ingredient catalog: personal rows, rows they created in a
 * household and the hints they wrote. Global (third-party) product data is not user data.
 */
export const exportedCatalogSchema = z.object({
    ingredients: z.array(z.object({
        id: uuidSchema,
        householdId: uuidSchema.nullable(),
        name: z.string(),
        aliases: z.array(z.string()),
        groceryCategory: z.string(),
    })),
    products: z.array(z.object({
        id: uuidSchema,
        householdId: uuidSchema.nullable(),
        barcode: z.string().nullable(),
        brand: z.string().nullable(),
        name: z.string(),
        ingredientsText: z.string().nullable(),
        nutriScore: z.string().nullable(),
    })),
    stores: z.array(z.object({
        id: uuidSchema,
        householdId: uuidSchema.nullable(),
        kind: z.string(),
        name: z.string(),
        lat: z.number().nullable(),
        lng: z.number().nullable(),
        street: z.string().nullable(),
        postalCode: z.string().nullable(),
        city: z.string().nullable(),
        countryCode: z.string().nullable(),
    })),
    hints: z.array(z.object({
        id: uuidSchema,
        householdId: uuidSchema.nullable(),
        target: z.string().nullable(),
        store: z.string().nullable(),
        verdict: z.string(),
        rating: z.number().int().nullable(),
        text: z.string(),
        createdAt: isoDateTimeSchema,
    })),
});
/** What was bought before – the basis of the shopping suggestions. */
export const exportedShoppingHistorySchema = z.object({
    name: z.string(),
    /** Null for the personal history. */
    householdName: z.string().nullable(),
    groceryCategory: z.string(),
    lastQuantity: decimalStringSchema.nullable(),
    lastUnit: z.string().nullable(),
    useCount: z.number().int(),
    lastUsedAt: isoDateTimeSchema,
    hidden: z.boolean(),
});
/** Products that were scanned or bought – what the devices keep for checking in the store. */
export const exportedProductUseSchema = z.object({
    productName: z.string(),
    brand: z.string().nullable(),
    barcode: z.string().nullable(),
    /** Null when recorded for the user alone. */
    householdName: z.string().nullable(),
    scanCount: z.number().int(),
    purchaseCount: z.number().int(),
    lastScannedAt: isoDateTimeSchema.nullable(),
    lastPurchasedAt: isoDateTimeSchema.nullable(),
});
/** Prices the user recorded, and personal ones (docs/partial/shopping-in-store-spec.md 7). */
export const exportedPriceSchema = z.object({
    productName: z.string().nullable(),
    ingredientName: z.string().nullable(),
    storeName: z.string().nullable(),
    householdName: z.string().nullable(),
    price: z.string(),
    currency: z.string(),
    perQuantity: z.string().nullable(),
    perUnitCode: z.string().nullable(),
    isPromotion: z.boolean(),
    depositAmount: z.string().nullable(),
    source: z.string(),
    observedAt: z.string(),
    note: z.string().nullable(),
});
/** Receipts the user uploaded, with their lines; never payment, loyalty or staff data. */
export const exportedReceiptSchema = z.object({
    storeName: z.string().nullable(),
    householdName: z.string().nullable(),
    purchasedAt: isoDateTimeSchema.nullable(),
    total: z.string().nullable(),
    currency: z.string().nullable(),
    status: z.string(),
    confirmedAt: isoDateTimeSchema.nullable(),
    createdAt: isoDateTimeSchema,
    /** File names inside the ZIP; empty once the photos were deleted. */
    images: z.array(z.string()),
    lines: z.array(z.object({
        rawText: z.string(),
        kind: z.string(),
        quantity: z.string().nullable(),
        unitPrice: z.string().nullable(),
        totalPrice: z.string(),
        productName: z.string().nullable(),
        ingredientName: z.string().nullable(),
        matchStatus: z.string(),
    })),
});
/** What a receipt text means at a chain – learned from corrections. */
export const exportedReceiptAliasSchema = z.object({
    text: z.string(),
    chainName: z.string().nullable(),
    householdName: z.string().nullable(),
    productName: z.string().nullable(),
    ingredientName: z.string().nullable(),
    useCount: z.number().int(),
});
/** Support conversations that were matched to the account, with what was written. */
export const exportedSupportConversationSchema = z.object({
    number: z.number().int(),
    subject: z.string(),
    status: z.string(),
    createdAt: isoDateTimeSchema,
    messages: z.array(z.object({
        direction: z.enum(['IN', 'OUT']),
        subject: z.string(),
        text: z.string(),
        createdAt: isoDateTimeSchema,
    })),
});
/** The application's own JSON export format. */
export const dataExportSchema = z.object({
    format: z.literal('recipes-manager'),
    formatVersion: z.literal(DATA_EXPORT_FORMAT_VERSION),
    exportedAt: isoDateTimeSchema,
    profile: userProfileSchema,
    recipes: z.array(recipeSchema),
    collections: z.array(collectionSchema.extend({ recipeIds: z.array(uuidSchema) })),
    cookingLog: z.array(cookingLogEntrySchema),
    /** Tasting notes (added in a backwards-compatible way; absent in older exports). */
    places: z.array(exportedPlaceSchema).default([]),
    visits: z.array(exportedVisitSchema).default([]),
    /** Ingredient catalog (absent in older exports). */
    catalog: exportedCatalogSchema.default({ ingredients: [], products: [], stores: [], hints: [] }),
    /** Shopping purchase history (absent in older exports). */
    shoppingHistory: z.array(exportedShoppingHistorySchema).default([]),
    /** Scanned and bought products (absent in older exports). */
    productUses: z.array(exportedProductUseSchema).default([]),
    /** Mail exchanged with the support (absent in older exports). */
    supportConversations: z.array(exportedSupportConversationSchema).default([]),
    /** Prices, receipts and receipt aliases (absent in older exports). */
    prices: z.array(exportedPriceSchema).default([]),
    receipts: z.array(exportedReceiptSchema).default([]),
    receiptAliases: z.array(exportedReceiptAliasSchema).default([]),
    /** Object keys inside the ZIP for each asset id. */
    media: z.array(z.object({ assetId: uuidSchema, fileName: z.string() })),
});
export const exportJobSchema = z.object({
    id: uuidSchema,
    status: z.enum(['QUEUED', 'RUNNING', 'READY', 'FAILED', 'EXPIRED']),
    downloadUrl: z.string().nullable(),
    sizeBytes: z.number().int().nullable(),
    expiresAt: isoDateTimeSchema.nullable(),
    createdAt: isoDateTimeSchema,
});
export const createExportRequestSchema = z.object({
    includeImages: z.boolean().default(true),
});
//# sourceMappingURL=data-export.js.map